import { intl } from '@fullstory/intl';
import { Event, SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { SegmentDetail } from '@fullstory/proto/fs/services/dx-objects';
import { Segment } from '@fullstory/proto/fs/services/search-settings';
import { SegmentSettings } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { isArray, isEqual } from 'lodash';
import { DEFAULT_SEGMENT } from './constants';

export function isCustomSearch(
  segment: SegmentDetail | undefined,
  segmentDefinition: SegmentDefinition | null,
): boolean {
  return !isEqual(segment?.segment?.segment?.segmentDefinition, segmentDefinition);
}

export function getNameFromSegmentScope(
  segment: SegmentDetail | undefined,
  segmentDefinition: SegmentDefinition | null,
): string {
  // Set the header title.
  let name =
    segment?.segment?.segment?.name ??
    intl.formatMessage({ defaultMessage: 'Unnamed Segment', id: 'aW9/Jd' });
  if (isCustomSearch(segment, segmentDefinition)) {
    name = intl.formatMessage(
      {
        defaultMessage: '{name} (Search)',
        id: '/XYV+o',
        description: 'Appended to name when the segment is a custom search.',
      },
      { name },
    );
  }
  return name;
}

export const getSegmentArrayFromVizSettings = (
  segment: SegmentSettings | SegmentSettings[] | null | undefined,
): SegmentSettings[] => {
  if (!segment) return [DEFAULT_SEGMENT];
  return isArray(segment) ? segment : [segment];
};

export const collectSpecsFromSegmentEvent = (event: Event, specs: string[]) => {
  if (event?.primary?.specEvent?.specId?.value) {
    specs.push(event.primary.specEvent.specId.value);
  }

  if (event?.primary?.seen?.withElementId?.spec) {
    specs.push(...event.primary.seen.withElementId.spec);
  }

  if (event?.primary?.visitedPage?.equals?.spec) {
    specs.push(...event.primary.visitedPage.equals.spec);
  }
  if (event?.primary?.visitedPage?.notEquals?.spec) {
    specs.push(...event.primary.visitedPage.notEquals.spec);
  }

  if (event?.primary?.eventSet) {
    if (event.primary.eventSet.union?.events?.length) {
      event.primary.eventSet.union.events.forEach(subEvent => {
        collectSpecsFromSegmentEvent(subEvent, specs);
      });
    }
    if (event.primary.eventSet.intersection?.events?.length) {
      event.primary.eventSet.intersection.events.forEach(subEvent => {
        collectSpecsFromSegmentEvent(subEvent, specs);
      });
    }
  }

  const dependencies = event?.deps || [];
  dependencies.forEach(dep => {
    if (dep.element?.withElementId?.spec) {
      specs.push(...dep.element.withElementId.spec);
    }

    if (dep.pageDefId?.equals?.spec) {
      specs.push(...dep.pageDefId.equals.spec);
    }
    if (dep.pageDefId?.notEquals?.spec) {
      specs.push(...dep.pageDefId.notEquals.spec);
    }
  });
};

export function extractSpecsFromSegment(segment: Segment | undefined | null): string[] {
  const specs: string[] = [];
  const segmentDefinition = segment?.segmentDefinition;
  if (!segmentDefinition) return specs;

  // Process behaviors (main events in the segment)
  if (segmentDefinition.behaviors && segmentDefinition.behaviors.length > 0) {
    segmentDefinition.behaviors.forEach(behavior => {
      if (behavior.event) {
        collectSpecsFromSegmentEvent(behavior.event, specs);
      }
    });
  }

  // Process exclude behaviors
  if (segmentDefinition.excludeBehaviors && segmentDefinition.excludeBehaviors.length > 0) {
    segmentDefinition.excludeBehaviors.forEach(behavior => {
      if (behavior.event) {
        collectSpecsFromSegmentEvent(behavior.event, specs);
      }
    });
  }
  return specs;
}
